import type { BasketItem } from '@scayle/storefront-nuxt'
import { useCurrentShop } from '#storefront/composables'
import {
  calculateEcommerceValue,
  mapAdditionalInfo,
  mapProductToEcommerceItem,
} from '~/utils'
import { useProductClickStorage } from '~/composables/useProductClickStorage'
import type {
  BasicViewData,
  EcommerceItem,
  ProductListData,
  TrackAddToBasketParams,
  TrackingEvent,
  TrackingPayload,
  TrackRemoveFromBasketParams,
} from '~/types/tracking'

const useBasketEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const currencyCode = useCurrentShop().value!.currency
  const { getProductClick } = useProductClickStorage()

  return {
    trackBasketItems: (
      items: BasketItem[] = [],
      label: 'page load' | 'cart update' = 'page load',
    ) => {
      // Convert BasketItem to EcommerceItem for consistent structure
      const ecommerceItems: EcommerceItem[] = items.map((basketItem) => {
        const additionalInfo = mapAdditionalInfo(
          {
            product: basketItem.product,
            quantity: basketItem.quantity,
            variant: basketItem.variant,
          },
          undefined,
        )

        // Get stored product click data from localStorage
        const storedProductClick = getProductClick(basketItem.product.id)

        return mapProductToEcommerceItem(
          basketItem.product,
          basketItem.variant,
          {
            ...additionalInfo,
            quantity: basketItem.quantity || 1,
            // Override item_list_id and item_list_name with stored values if available
            ...(storedProductClick && {
              item_list_id: storedProductClick.item_list_id,
              item_list_name: storedProductClick.item_list_name,
            }),
          },
          undefined,
        )
      })

      track('cart_summary', {
        cart_status: {
          items: ecommerceItems,
        },
        category: 'ecommerce',
        action: 'cart summary',
        label,
        _clear: true,
      })
    },

    trackAddToBasket: ({
      products,
      product,
      quantity = 1,
      variant,
      index = 1,
      list,
      actionType,
      location,
    }: TrackAddToBasketParams) => {
      // Get stored product click data from localStorage for single product
      let enhancedList = list
      if (product && !list) {
        const storedProductClick = getProductClick(product.id)
        if (storedProductClick) {
          enhancedList = {
            id: storedProductClick.item_list_id,
            name: storedProductClick.item_list_name,
          }
        }
      }

      // If we have products array, enhance with localStorage data
      let enhancedProducts = products
      if (products && products.length > 0) {
        enhancedProducts = products.map((prod) => {
          const storedProductClick = getProductClick(prod.product_id)
          if (storedProductClick) {
            return {
              ...prod,
              item_list_id: storedProductClick.item_list_id,
              item_list_name: storedProductClick.item_list_name,
            }
          }
          return prod
        })
      }

      track('add_to_cart', {
        ...(product && { product: { ...product, index } }),
        products: enhancedProducts,
        variant,
        quantity,
        currencyCode,
        list: enhancedList,
        actionType,
        location,
      })
    },

    trackRemoveFromBasket: ({
      product,
      products,
      quantity,
      variant,
      index = 1,
      location,
    }: TrackRemoveFromBasketParams) => {
      track('remove_from_cart', {
        ...(product && { product: { ...product, index } }),
        products,
        variant,
        quantity,
        currencyCode,
        ...(location && { location }),
      })
    },

    trackBasket: (
      items: ProductListData[],
      label: 'page load' | 'cart update' = 'page load',
    ) => {
      // Convert ProductListData to EcommerceItem
      const ecommerceItems: EcommerceItem[] = items.map((payload) => {
        const additionalInfo = mapAdditionalInfo(payload, undefined)

        // Get stored product click data from localStorage
        const storedProductClick = getProductClick(payload.product.id)

        return mapProductToEcommerceItem(
          payload.product,
          payload.variant,
          {
            ...additionalInfo,
            quantity: payload.quantity || 1,
            // Override item_list_id and item_list_name with stored values if available
            ...(storedProductClick && {
              item_list_id: storedProductClick.item_list_id,
              item_list_name: storedProductClick.item_list_name,
            }),
          },
          undefined,
        )
      })

      track('cart_summary', {
        cart_status: {
          currency: currencyCode,
          value: calculateEcommerceValue(ecommerceItems),
          items: ecommerceItems,
        },
        category: 'ecommerce',
        action: 'cart summary',
        label,
        _clear: true,
      })
    },

    trackViewBasket: (items: ProductListData[], pagePayload: BasicViewData) => {
      // Convert ProductListData to EcommerceItem
      const ecommerceItems: EcommerceItem[] = items.map((payload) => {
        const additionalInfo = mapAdditionalInfo(payload, undefined)

        // Get stored product click data from localStorage
        const storedProductClick = getProductClick(payload.product.id)

        return mapProductToEcommerceItem(
          payload.product,
          undefined,
          {
            ...additionalInfo,
            quantity: payload.quantity || 1,
            // Override item_list_id and item_list_name with stored values if available
            ...(storedProductClick && {
              item_list_id: storedProductClick.item_list_id,
              item_list_name: storedProductClick.item_list_name,
            }),
          },
          undefined,
        )
      })

      const { action, ...pageData } = pagePayload
      track('view_cart', {
        ecommerce: {
          currency: currencyCode,
          value: calculateEcommerceValue(ecommerceItems),
          items: ecommerceItems,
        },
        category: 'ecommerce',
        action: 'view_cart',
        ...pageData,
      })
    },

    trackBasketInStock: (size: string, productId?: number) => {
      track('back_in_stock', {
        category: 'product',
        action: 'click on notify me',
        label: size,
        item_id: productId,
      })
    },
  }
}
export default useBasketEvents
